import React from 'react';

import { useTranslate } from '@/core/locales';
import { fNumber } from '@/core/utils/format-number';
import { TRANSLATION_NAMESPACES } from '@/core/constants';
import { SERVICES_CONFIG } from '@/core/constants/services';
import getCurrencySymbolByCode from '@/core/utils/get-currency-symbol';

import { Iconify } from '@/components/iconify';
import { Tooltip } from '@/components/ui/Tooltip';
import { Box, Button, Typography } from '@/components/ui';

import { RouterLink } from '@/routes/components';

type UnitCardProps = {
  unit: any;
  currentTariff: any;
};

export const UnitCard: React.FC<UnitCardProps> = ({ unit, currentTariff }: UnitCardProps) => {
  const { t } = useTranslate(TRANSLATION_NAMESPACES.COMMON);
  const currencySymbol = getCurrencySymbolByCode(currentTariff?.currencyCode);

  const serviceUrl =
    SERVICES_CONFIG[unit.technicalName as keyof typeof SERVICES_CONFIG]?.url ?? '#';

  const getFeatureText = (feature: any, limitations?: any[]): string => {
    const limitation = limitations?.find((l) => l.featureId === feature.id);

    const durationUnitMap: Record<string, string> = {
      day: t('features.durations.day'),
      month: t('features.durations.month'),
      year: t('features.durations.year'),
      unlimited: t('features.durations.unlimited'),
    };

    const translatedDurationUnit = limitation?.durationUnit
      ? durationUnitMap[limitation.durationUnit]
      : undefined;

    return t(`features.${feature.slug}`, {
      count: limitation?.value ?? 0,
      durationUnit: translatedDurationUnit,
    });
  };

  const renderUnitDescription = (description: string) => (
    <Box className="mt-4 text-[var(--color-black)]">
      <Typography variant="body2" className="text-sm leading-relaxed text-[var(--color-black)]">
        {description}
      </Typography>
    </Box>
  );

  const features: any[] = unit?.features ?? [];

  return (
    <Box className="relative p-6 rounded-3xl bg-[var(--color-white)] hover:shadow-md transition-shadow duration-300 flex flex-col h-full">
      <Box className="flex justify-between items-start">
        <Box>
          <Box className="flex items-center gap-2 mb-2">
            <Typography variant="h4" className="font-semibold text-xl text-[var(--color-grey-500)]">
              {unit.name}
            </Typography>

            {features.length > 0 && (
              <Box className="flex items-center gap-1">
                {features.map((feature: any) => {
                  const featureText = getFeatureText(feature, currentTariff.limitations);

                  const renderFeatureText =
                    feature.slug === 'auto-discount'
                      ? featureText.split('<----->').map((part, index) => (
                          <Typography variant="body2" key={index} className="mb-2">
                            {part.trim()}
                          </Typography>
                        ))
                      : featureText;

                  const featureTooltipContent = (
                    <Box>
                      <Typography variant="caption" className="font-extrabold block mb-1">
                        {t('subscriptions.limit')}
                      </Typography>

                      <Typography variant="body2">{renderFeatureText}</Typography>
                    </Box>
                  );

                  return (
                    <Tooltip key={feature.name} title={featureTooltipContent}>
                      <Box className="flex items-center justify-center w-5 h-5 rounded-full cursor-pointer text-[var(--color-grey-500)] hover:text-[var(--color-grey-700)] transition-colors">
                        <Iconify icon="eva:info-outline" width={16} />
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            )}
          </Box>

          <Box>
            <Typography className="text-md font-medium">
              {t('bundles.total')}
              {fNumber(
                currentTariff.discountPrice ? currentTariff.discountPrice : currentTariff.price
              )}{' '}
              {currencySymbol}
            </Typography>

            <Typography variant="h2" className="my-1">
              {fNumber(currentTariff?.minimalPeriodPrice)} {currencySymbol}/ {t('bundles.mon')}
            </Typography>

            {(currentTariff.discountPrice || currentTariff.periodicDiscount) && (
              <Typography variant="h5" className="text-[var(--color-grey-400)] line-through">
                {fNumber(currentTariff?.originalMinimalPeriodPrice)} {currencySymbol}/{' '}
                {t('bundles.mon')}
              </Typography>
            )}
          </Box>
        </Box>

        {currentTariff?.discount > 0 && (
          <Box className="border bg-[var(--color-secondary)] text-[var(--color-white)] shadow-md rounded-lg font-bold flex items-center px-3 py-1 whitespace-nowrap text-xs sm:text-sm md:text-base">
            {t('bundles.unitDiscount', { discount: currentTariff?.discount })}
          </Box>
        )}
      </Box>

      {renderUnitDescription(unit.description)}

      <Box className="mt-auto pt-6 flex items-center gap-4">
        <Button
          variant="contained"
          href="/seller/billing/tariffs"
          color={unit.color || 'success'}
          className="rounded-xl py-3 px-7 font-bold text-sm normal-case"
        >
          {t('bundles.pay')}
        </Button>
        <RouterLink href={serviceUrl} className="text-sm text-[var(--color-black)] hover:underline">
          {t('bundles.learnMore')}
        </RouterLink>
      </Box>
    </Box>
  );
};
